/**
 * Typed client for the AGENT EMAIL read surface (`mail.*` gateway methods) — the
 * data layer behind the Agent-page EMAIL tab (C3).
 *
 * RECONCILED 2026-07-21 to the shipped runtime contract (commit `9a5967b4`,
 * `extensions/lexiun-core/src/gateway-methods/mail.ts`): `mail.mailboxes`,
 * `mail.list`, `mail.get`, `mail.read_attachment`. Read-only by construction —
 * no write verb; keyed on the RFC `Message-ID`, never a positional himalaya
 * envelope id (those shift under every sync).
 *
 * Notes from the shipped shapes:
 *  - the `before`/`nextBefore` cursor is an **ISO date string**;
 *  - `mail.mailboxes`/`mail.list` also carry `credentialsConfigured` — when no
 *    mailbox creds are configured the runtime returns an EMPTY mailbox/message
 *    list, which the pane renders as its honest "no mailboxes" state;
 *  - `mail.list` rows ship an EMPTY `snippet` (the body read is deferred to
 *    `mail.get`), so the list hides an empty snippet line;
 *  - `mail.get` returns no `cc`/`bodyHtml` (both stay optional here).
 */
import { useGatewayStore } from "@/stores/useGatewayStore";

/** A selectable mailbox/folder (`mail.mailboxes` row). */
export interface Mailbox {
  /** Canonical id the runtime keys `mail.list` on: `inbox` | `sent` | `spam`. */
  id: string;
  /** Human label the runtime resolved (e.g. "Caixa de entrada"). */
  name: string;
  /**
   * Messages the folder holds. Shipped runtime-side (`4403de2`), so the nav
   * badge shows it — on a MONITORING surface the useful number is "how many
   * emails are here", and it matches the list the lawyer is looking at.
   */
  total?: number;
  /**
   * IMAP `\Seen`-derived unread count. Also populated, but deliberately NOT the
   * badge: "unread" is a human-inbox concept that reads as noise on a read-only
   * view of an AGENT's mail (its sent items being "unread" is meaningless, and a
   * badge that disagrees with the visible list count looks like a bug).
   */
  unread?: number;
}

export interface MailboxesResult {
  mailboxes: Mailbox[];
  /** `false` ⇒ no mailbox creds configured (mailboxes is then empty). */
  credentialsConfigured?: boolean;
}

/** One message envelope in a mailbox list (`mail.list` row). */
export interface MailEnvelope {
  /** RFC Message-ID — THE durable key (envelope ids are positional). */
  messageId: string;
  from: string;
  to?: string;
  subject: string;
  /** Epoch ms, for newest-first ordering + display. */
  date: number;
  /** Bounded preview, ellipsised. */
  snippet: string;
  hasAttachments: boolean;
  unread?: boolean;
}

/** A full message (`mail.get`). */
export interface MailMessage {
  messageId: string;
  from: string;
  to?: string;
  cc?: string;
  subject: string;
  date: number;
  bodyText: string;
  bodyHtml?: string;
  attachments: { name: string; mime?: string; sizeBytes?: number }[];
}

/** Opaque backward-paging cursor (round-tripped; the runtime owns its meaning). */
export type MailCursor = string;

export interface MailListParams {
  mailbox: string;
  before?: MailCursor;
  limit?: number;
}

export interface MailListResult {
  mailbox: string;
  messages: MailEnvelope[];
  /** Cursor for the next older page; ABSENT ⇒ the head is reached. */
  nextBefore?: MailCursor;
  credentialsConfigured?: boolean;
  /**
   * Rows dropped from THIS page because their Message-ID was unresolvable
   * (the runtime's poison-isolation: one unreadable message must not fail the
   * whole page). MUST be surfaced — a short page that looks like a small folder
   * is a lie on a monitoring surface. Added runtime-side in `8e2c1d89` at this
   * lane's request.
   */
  skipped?: number;
  /** Envelopes the folder holds in total, before `limit`/`before` paging. */
  total?: number;
}

/** Served attachment bytes (`mail.read_attachment`), mirroring lx.chat_read_media. */
export interface MailAttachmentBytes {
  name: string;
  mime?: string;
  bytesBase64: string;
  sizeBytes: number;
}

function rpc() {
  const client = useGatewayStore.getState().rpc;
  if (!client) throw new Error("gateway not connected");
  return client;
}

const asParams = (o: unknown): Record<string, unknown> => o as Record<string, unknown>;

/**
 * `mail.list`/`mail.get`/`mail.read_attachment` are legitimately slow: the
 * runtime shells out to himalaya, and the FIRST list of a folder derives an RFC
 * Message-ID with one `message export` per row (~3.6 s/row cold on galvão;
 * re-lists are memoized and cost ~5 s). The 30 s client default would reject a
 * realistic page long before the server is done, so these three override it.
 *
 * This is purely about DURATION. The old "any page ≥2 rows drops the socket"
 * failure was a different bug entirely — the gateway had no `ping` handler, so
 * every heartbeat went unanswered and any long request died with it. Fixed
 * runtime-side (`f8dfe341`) and hardened here (`heartbeatFailureIsFatal` in
 * `lib/gateway-client.ts`), so a slow call is now merely slow.
 */
const MAIL_SLOW_TIMEOUT_MS = 120_000;

/** `mail.mailboxes` — the selectable folders (inbox / sent / spam). */
export function listMailboxes(): Promise<MailboxesResult> {
  return rpc().call<MailboxesResult>("mail.mailboxes", {});
}

/** `mail.list` — envelopes for one mailbox, newest first, paged backwards. */
export function listMail(params: MailListParams): Promise<MailListResult> {
  return rpc().call<MailListResult>("mail.list", asParams(params), MAIL_SLOW_TIMEOUT_MS);
}

/** `mail.get` — one full message, keyed on the RFC Message-ID. */
export function getMail(messageId: string): Promise<{ message: MailMessage }> {
  return rpc().call<{ message: MailMessage }>(
    "mail.get",
    asParams({ messageId }),
    MAIL_SLOW_TIMEOUT_MS,
  );
}

/** `mail.read_attachment` — bytes for one attachment (18 MB cap, base64). */
export function readMailAttachment(
  messageId: string,
  name: string,
): Promise<MailAttachmentBytes> {
  return rpc().call<MailAttachmentBytes>(
    "mail.read_attachment",
    asParams({ messageId, name }),
    MAIL_SLOW_TIMEOUT_MS,
  );
}
